/**
 * URL block (spec §7.4.15, M25): which addresses may be links, and what a click does with one.
 */

export type UrlAction = "browser" | "copy" | "system";
export type OpenIn = "tab" | "window";

/** Schemes that run or embed content in this origin, or that browsers block from a web page (Q76). */
const REFUSED = new Set(["javascript", "data", "vbscript", "blob", "file"]);
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

export type UrlCheck = { ok: true; url: string; scheme: string; web: boolean } | { ok: false; reason: "urlBlock.empty" | "urlBlock.noScheme" | "urlBlock.refused"; scheme?: string };

/** Whether `raw` may be shown as a link: a scheme is required, and the refused ones never pass. */
export function checkUrl(raw: string): UrlCheck {
  const url = raw.trim();
  if (!url) return { ok: false, reason: "urlBlock.empty" };
  const m = SCHEME.exec(url);
  if (!m) return { ok: false, reason: "urlBlock.noScheme" };
  const scheme = m[1]!.toLowerCase();
  if (REFUSED.has(scheme)) return { ok: false, reason: "urlBlock.refused", scheme };
  return { ok: true, url, scheme, web: scheme === "http" || scheme === "https" };
}

/** A typed address without a scheme that looks like a host gets `https://`, as the address bar does. */
export function withScheme(raw: string): string {
  const url = raw.trim();
  if (!url || /\s/.test(url)) return url;
  // a host first, so `example.org:8080` is not read as a scheme named `example.org`
  if (/^[\w-]+(\.[\w-]+)+(:\d+)?([/?#]|$)/.test(url)) return `https://${url}`;
  return url;
}

/** Do what the block says with a checked address. Returns what happened, for the reader's feedback. */
export async function follow(url: string, action: UrlAction, openIn: OpenIn, web: boolean): Promise<"opened" | "copied" | "handed"> {
  if (action === "copy") {
    await navigator.clipboard.writeText(url);
    return "copied";
  }
  if (action === "system" && !web) {
    // an app link (mailto:, zotero:, …): the browser hands it to the app the system registered
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener noreferrer";
    a.click();
    return "handed";
  }
  // a web address: the system's handler for it is the browser, so `system` opens it like `browser`
  const features = openIn === "window" ? "noopener,noreferrer,popup,width=1100,height=800" : "noopener,noreferrer";
  window.open(url, "_blank", features);
  return "opened";
}
